import { Component, OnInit } from '@angular/core';
import { DashboardService } from '../../dashboard.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ConfirmDialogComponent } from 'src/app/components/confirm-dialog/confirm-dialog.component';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { forkJoin } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-reminders',
  templateUrl: './reminders.component.html',
  styleUrls: ['./reminders.component.scss']
})
export class RemindersComponent implements OnInit {
  isPremium: boolean = true;

  hours = Array.from({ length: 12 }, (_, i) => i + 1);
  periods = ['AM', 'PM'];

  reminders: any[] = [
    {
      id: 1,
      name: 'Initial Follow-up',
      isEnabled: true,
      delayValue: 1,
      delayUnit: 'Days',
      sendAtTime: '10:00 AM',
      channels: ['WHATSAPP'],
      whatsappTemplate: 'wa_followup_1',
      emailTemplate: 'email_followup_1'
    }
  ];

  whatsappTemplates: any[] = [];

  emailTemplates: any[] = [];

  previewTemplateId: string = '';
  previewType: 'WHATSAPP' | 'EMAIL' | '' = '';
  previewReminderId: string | number | null = null;
  previewOffset: number = 0;

  isLoading: boolean = false;
  isSaving: boolean = false;

  constructor(
    private dashboardService: DashboardService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private sanitizer: DomSanitizer
  ) {
    this.initializeReminders();
  }

  ngOnInit() {
    this.loadTemplates();
    this.loadReminders();
  }

  loadTemplates() {
    forkJoin({
      email: this.dashboardService.getAvailableReminderEmailTemplates(),
      whatsapp: this.dashboardService.getAvailableReminderWhatsappTemplates(),
      profile: this.dashboardService.getAdminProfile({ 'skip-loader': 'true' })
    }).subscribe({
      next: (res: any) => {
        // Use cached profile data (getAdminProfile uses shareReplay(1) - no extra HTTP call)
        const profile = res.profile;
        if (profile) {
          const companyName = profile.companyName || profile.name || profile.company_name || profile.company || 'Your Company';
          const logo = profile.logo || '/assets/logo.png';
          const phone = profile.phoneNumber || profile.phone || '+91 9876543210';

          this.templateVariables['COMPANY_NAME'] = companyName;
          this.templateVariables['enterpriseName'] = companyName;
          this.templateVariables['company_name'] = companyName;
          this.templateVariables['ORG_PHONE_NO'] = phone;
          this.templateVariables['LOGO_URL'] = logo;
        }

        this.whatsappTemplates = (res.whatsapp?.templates || []).map(t => ({
          id: t._id,
          name: t.templateName,
          technicalId: t.template_id,
          content: t.content || ''
        }));

        this.emailTemplates = (res.email?.templates || []).map(t => ({
          id: t._id,
          name: t.templateName,
          subject: t.subject || '',
          content: t.content || ''
        }));

        // If reminders were loaded before templates, ensure they have valid defaults if empty
        if (this.whatsappTemplates.length > 0) {
          this.reminders.forEach(r => {
            if (r.channels.includes('WHATSAPP') && !r.whatsappTemplate) {
              r.whatsappTemplate = this.whatsappTemplates[0].id;
            }
          });
        }
      },
      error: (err) => {
        console.error('Failed to load templates:', err);
        this.snackBar.open('Error loading templates', 'Close', { duration: 3000 });
      }
    });
  }

  loadReminders() {
    this.isLoading = true;
    this.dashboardService.getReminderTemplates().subscribe({
      next: (res: any) => {
        const templates = res.templates || [];
        this.reminders = templates.map((r: any) => this.mapBackendToFrontend(r));
        this.isLoading = false;
        this.initializeReminders();
      },
      error: () => this.isLoading = false
    });
  }

  mapBackendToFrontend(r: any) {
    // sendAtHour is null when unit is 'Minutes' - treat null as missing, use default
    const sendAtHour = r.timing.sendAtHour;
    const timeStr = (sendAtHour !== undefined && sendAtHour !== null)
      ? `${sendAtHour > 12 ? sendAtHour - 12 : (sendAtHour === 0 ? 12 : sendAtHour)}:00 ${sendAtHour >= 12 ? 'PM' : 'AM'}`
      : '10:00 AM';

    const channels = [];
    if (r.channels?.email?.enabled) channels.push('EMAIL');
    if (r.channels?.whatsapp?.enabled) channels.push('WHATSAPP');

    return {
      id: r._id,
      name: r.name,
      isEnabled: r.isEnabled,
      delayValue: r.timing.value,
      delayUnit: r.timing.unit === 'Minutes' ? 'Minutes' : (r.timing.unit === 'Hours' ? 'Hours' : 'Days'),
      sendAtTime: timeStr,
      channels: channels,
      whatsappTemplate: r.channels?.whatsapp?.templateId?._id || r.channels?.whatsapp?.templateId || (this.whatsappTemplates.length > 0 ? this.whatsappTemplates[0].id : null),
      emailTemplate: r.channels?.email?.templateId?._id || r.channels?.email?.templateId || (this.emailTemplates.length > 0 ? this.emailTemplates[0].id : null),
      isModified: false
    };
  }

  mapFrontendToBackend(r: any) {
    // Validate reminder name
    if (!r.name || r.name.trim().length === 0) {
      throw new Error('Reminder name is required');
    }

    // ENHANCED VALIDATION: Ensure time values are properly set
    // Skip time validation for Minutes unit - backend doesn't use sendAtHour for it
    if (r.delayUnit !== 'Minutes' && (!r.tempHour || !r.tempPeriod)) {
      console.error('Time not properly set for reminder:', r);
      throw new Error('Time not properly set. Please select both hour and period.');
    }

    const hourVal = parseInt(r.tempHour, 10);

    // Validate hour range
    if (isNaN(hourVal) || hourVal < 1 || hourVal > 12) {
      console.error('Invalid hour value:', r.tempHour);
      throw new Error('Invalid hour value. Please select a valid hour (1-12).');
    }

    // Convert 12-hour to 24-hour format
    let hour = hourVal;
    if (r.tempPeriod === 'PM' && hourVal !== 12) {
      hour = hourVal + 12;
    } else if (r.tempPeriod === 'AM' && hourVal === 12) {
      hour = 0; // Midnight
    }

    // Final validation
    if (hour < 0 || hour > 23) {
      console.error('Calculated hour out of range:', hour);
      throw new Error('Invalid time calculation. Please try again.');
    }

    return {
      name: r.name,
      isEnabled: r.isEnabled,
      timing: {
        value: r.delayValue,
        unit: r.delayUnit,
        relativeTo: 'INVITATION', // Default for now
        sendAtHour: r.delayUnit === 'Minutes' ? null : hour
      },
      channels: {
        email: {
          enabled: r.channels.includes('EMAIL'),
          templateId: r.emailTemplate
        },
        whatsapp: {
          enabled: r.channels.includes('WHATSAPP'),
          templateId: r.whatsappTemplate
        }
      }
    };
  }

  initializeReminders() {
    this.reminders.forEach(r => this.parseTime(r));
    // Default select first reminder WhatsApp for preview -> DISABLED per user request
    // if (this.reminders.length > 0) {
    //   const first = this.reminders[0];
    //   if (first.channels.includes('WHATSAPP')) {
    //     this.previewTemplate(first.whatsappTemplate, 'WHATSAPP', first.id);
    //   } else if (first.channels.includes('EMAIL')) {
    //     this.previewTemplate(first.emailTemplate, 'EMAIL', first.id);
    //   }
    // }
  }

  parseTime(reminder: any) {
    if (reminder.sendAtTime) {
      const parts = reminder.sendAtTime.split(' ');
      if (parts.length === 2) {
        const timeParts = parts[0].split(':');
        reminder.tempHour = parseInt(timeParts[0], 10);
        reminder.tempPeriod = parts[1];
        // Default minutes to 00 for simplicity as per request for 1-12
      }
    } else {
      reminder.tempHour = 10;
      reminder.tempPeriod = 'AM';
    }
    reminder.isModified = false;
  }

  updateTime(reminder: any) {
    if (reminder.tempHour && reminder.tempPeriod) {
      reminder.sendAtTime = `${reminder.tempHour}:00 ${reminder.tempPeriod}`;
    }
    this.markModified(reminder);
  }

  markModified(reminder: any) {
    reminder.isModified = true;
  }

  addReminder() {
    if (this.reminders.length >= 10) return;

    const newReminder = {
      id: null, // New
      name: `Reminder ${this.reminders.length + 1}`,
      isEnabled: true,
      delayValue: 1,
      delayUnit: 'Days' as 'Days' | 'Hours' | 'Minutes',
      sendAtTime: '10:00 AM',
      channels: ['EMAIL'],
      whatsappTemplate: this.whatsappTemplates[0]?.id,
      emailTemplate: this.emailTemplates[0]?.id,
      tempHour: 10,
      tempPeriod: 'AM',
      isModified: true
    };

    if (this.reminders.some(r => r.id === null)) {
      this.snackBar.open('Please save the current new reminder first', 'Close', { duration: 3000 });
      return;
    }

    this.reminders.push(newReminder);
  }

  removeReminder(index: number) {
    const reminderName = this.reminders[index].name;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      panelClass: 'delete-dialog-container',
      data: {
        title: 'Delete Reminder Rule',
        message: `Are you sure you want to delete "${reminderName}"? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        danger: true
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'confirm') {
        const reminder = this.reminders[index];
        if (reminder.id) {
          this.dashboardService.deleteReminderTemplate(reminder.id).subscribe(() => {
            this.reminders.splice(index, 1);
            this.snackBar.open('Reminder deleted', 'Close', { duration: 2000 });
          });
        } else {
          this.reminders.splice(index, 1);
        }
      }
    });
  }

  toggleChannel(reminder: any, channel: string) {
    if (!reminder.channels) reminder.channels = [];

    const idx = reminder.channels.indexOf(channel);
    if (idx > -1) {
      // Prevent removing if it's the last one
      if (reminder.channels.length === 1) {
        this.snackBar.open('At least one channel must remain enabled', 'Close', { duration: 2500 });
        return;
      }
      reminder.channels.splice(idx, 1);
    } else {
      reminder.channels.push(channel);
    }
    this.markModified(reminder);
  }

  previewTemplate(templateId: string, type: 'WHATSAPP' | 'EMAIL' | '', reminderId: string | number) {
    this.previewTemplateId = templateId;
    this.previewType = type;
    this.previewReminderId = reminderId;

    // Calculate offset to align preview with the active card
    setTimeout(() => {
      const index = this.reminders.findIndex(r => r.id === reminderId);
      if (index !== -1) {
        const element = document.getElementById(`reminder-card-${index}`);
        if (element) {
          // Align Center of Preview with Center of Card
          const cardCenter = element.offsetTop + (element.offsetHeight / 2);

          // Estimate preview height or use fixed value. 
          // Phone preview is ~600px + labels ~50px = 650px. Half is 325px.
          // User adjusted it to -500 previously? That would pull it UP significantly.
          // If they want "Center of Screen", using -300 puts content center at card center.
          const targetTop = cardCenter - 300;

          this.previewOffset = Math.max(0, targetTop);
        }
      }
    });

  }

  getTemplateName(id: string): string {
    if (!id) return '';
    const wa = this.whatsappTemplates.find(t => t.id === id);
    if (wa) return wa.name;
    const email = this.emailTemplates.find(t => t.id === id);
    if (email) return email.name;
    return id;
  }

  isDaysInvalid(reminder: any): boolean {
    return reminder.delayUnit === 'Days' && reminder.delayValue > 20;
  }

  // Template preview helpers - Variables aligned with backend payload (reminders/module.ts)
  private templateVariables: { [key: string]: string } = {
    // UPPER_CASE (Primary format)
    'COMPANY_NAME': 'Acme Corporation',
    'CANDIDATE_NAME': 'John Smith',
    'ASSESSMENT_NAME': 'Senior Developer Assessment',
    'TOKEN': 'ABC-123-XYZ',
    'LINK': 'https://evaltrix.com/assessment/12345',
    'DEADLINE': 'Feb 10, 2026',
    'PRODUCT_NAME': 'Evaltrix',

    // camelCase (Backend uses both)
    'candidateName': 'John Smith',
    'enterpriseName': 'Acme Corporation',
    'assessmentName': 'Senior Developer Assessment',
    'inviteLink': 'https://evaltrix.com/assessment/12345',
    'deadline': 'Feb 10, 2026',
    'link': 'https://evaltrix.com/assessment/12345',
    'jobTitle': 'Senior Developer',
    'role': 'Senior Developer',

    // snake_case (Legacy compatibility)
    'candidate_name': 'John Smith',
    'company_name': 'Acme Corporation',
    'job_title': 'Senior Developer',

    // Other legacy formats
    'Candidate Name': 'John Smith',
    'POSITION_GIVEN_IN_ASSESSMENT': 'Senior Developer',
    'Job / Assessment Name': 'Senior Developer Assessment',
    'Assessment Name': 'Senior Developer Assessment',
    'Assessment Link': 'https://evaltrix.com/assessment/12345',
    'DURATION': '60 minutes',
    'Due Date': 'Feb 10, 2026',
    'Deadline': 'Feb 10, 2026',
    'ASSESSMENT_TYPE': 'Coding Challenge',
    'PRODUCT_LINK': 'https://evaltrix.com',
    'USER_NAME': 'Jane Doe',
    'DOMAIN': 'https://evaltrix.com/',
    'company_phone': '+1-234-567-8900',
    'COMPANY_EMAIL': 'support@evaltrix.com',
    'ORG_PHONE_NO': '+91 9876543210'
  };

  private replaceTemplateVariables(content: string): string {
    if (!content) return '';
    let result = content;

    // Handle logo replacement with HTML for preview
    const logoUrl = this.templateVariables['LOGO_URL'] || '/assets/logo.png';
    const logoHtml = `<div style="display: flex; justify-content: center; margin-bottom: 24px;"><img src="${logoUrl}" style="max-width: 140px; max-height: 70px; object-fit: contain;"></div>`;
    result = result.replace(/{{LOGO}}/gi, logoHtml);

    Object.entries(this.templateVariables).forEach(([key, value]) => {
      result = result.replace(new RegExp(`{{${key}}}`, 'gi'), value);
    });
    return result;
  }

  getPreviewSubject(): string {
    if (this.previewType !== 'EMAIL' || !this.previewTemplateId) return 'Reminder...';
    const template = this.emailTemplates.find(t => t.id === this.previewTemplateId);
    if (!template) return 'Reminder...';
    const subject = template.subject || template.name;
    return this.replaceTemplateVariables(subject);
  }

  getPreviewContent(): SafeHtml {
    if (!this.previewTemplateId) return '';
    let rawContent = '';
    if (this.previewType === 'EMAIL') {
      const template = this.emailTemplates.find(t => t.id === this.previewTemplateId);
      rawContent = template ? this.replaceTemplateVariables(template.content) : '';
    } else {
      const template = this.whatsappTemplates.find(t => t.id === this.previewTemplateId);
      rawContent = template ? this.replaceTemplateVariables(template.content) : '';
    }
    return this.sanitizer.bypassSecurityTrustHtml(rawContent);
  }

  showNotInterested(templateId: string): boolean {
    if (!templateId || this.previewType !== 'WHATSAPP') return false;
    const template = this.whatsappTemplates.find(t => t.id === templateId);
    if (!template) return false;

    const techId = template.technicalId || '';
    return techId === 'reminder_follow_up_no_image' || techId === 'final_reminder_follow_up';
  }

  saveReminder(reminder: any) {
    if (this.isDaysInvalid(reminder)) return;

    this.isSaving = true;
    const payload = this.mapFrontendToBackend(reminder);

    const obs = reminder.id
      ? this.dashboardService.updateReminderTemplate(reminder.id, payload)
      : this.dashboardService.createReminderTemplate(payload);

    obs.subscribe({
      next: (res: any) => {
        reminder.id = res.template?._id || res.template?.id || res._id;
        reminder.isModified = false;
        reminder.saveError = null; // Clear any previous errors
        this.isSaving = false;
        this.snackBar.open('Reminder saved successfully', 'Close', {
          duration: 3000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });
      },
      error: (err) => {
        this.isSaving = false;

        // ENHANCED ERROR HANDLING: Differentiate between network and validation errors
        const isNetworkError = err.status === 0 || err.status >= 500;
        const errorMessage = err.error?.message || (isNetworkError ? 'Network error. Please try again.' : 'Error saving reminder');

        reminder.saveError = errorMessage;

        if (isNetworkError) {
          // Network error - offer retry
          const snackBarRef = this.snackBar.open(errorMessage, 'Retry', {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });

          snackBarRef.onAction().subscribe(() => {
            this.saveReminder(reminder);
          });
        } else {
          // Validation or business logic error
          this.snackBar.open(errorMessage, 'Close', {
            duration: 4000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        }
      }
    });
  }


}
